import * as React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PublicIcon from '@mui/icons-material/Public';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import ArticleIcon from '@mui/icons-material/Article';
import HomeIcon from '@mui/icons-material/Home';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import QuizIcon from '@mui/icons-material/Quiz';
import CategoryIcon from '@mui/icons-material/Category';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GavelIcon from '@mui/icons-material/Gavel';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WebIcon from '@mui/icons-material/Web';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, IconButton, MenuItem, MenuList, Popover, Tooltip, styled } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import ApplicationAppService from '../application/platform/application-app-service';

const listItemButtonStyle = ({ theme }: { theme: any }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 1.5),
  padding: theme.spacing(1.25, 2),
  transition: 'all 0.2s ease-in-out',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.contrastText,
    },
  },
});

// Item de navegação: renderiza o Link do router (o ListItemButton sozinho vira uma div e ignora o "to")
const ListItemButtonLink = styled(ListItemButton)(listItemButtonStyle) as typeof ListItemButton;

const ListItemButtonToggle = styled(ListItemButton)(listItemButtonStyle);

const applicationData = ApplicationAppService.getApplicationData();

const slugifyCityName = (city: string) =>
  city.normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLowerCase().replace(/[^a-z0-9]/g, '');

function getTransparencyPortalPath(): string | undefined {
  const authenticationData = ApplicationAppService.getAuthenticationData();
  const company = authenticationData?.company;
  if (!company?.state || !company?.city) return undefined;

  const uf = company.state.toLowerCase();
  const entityPrefix = authenticationData?.type === 2 ? 'cm' : 'pm';
  const citySlug = `${entityPrefix}${slugifyCityName(company.city)}`;

  return `/${uf}/${citySlug}/transparencia`;
}

function NavTooltip({ title, open, children }: { title: string; open: boolean; children: React.ReactElement }) {
  return (
    <Tooltip title={title} placement="right" disableHoverListener={open}>
      {children}
    </Tooltip>
  );
}

// A rota atual pertence ao item quando é a própria rota ou uma sub-rota (ex.: /emendas/123, /paginas/editar/5)
export function isPathActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

interface NavGroupItem {
  label: string;
  path: string;
  icon: React.ReactElement;
}

// Item da barra lateral que abre um submenu flutuante com telas correlatas
function NavGroup({ title, icon, items, open }: { title: string; icon: React.ReactElement; items: NavGroupItem[]; open: boolean }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

  // Item mais específico que corresponde à rota (ex.: /perguntas-respostas/categorias vence /perguntas-respostas)
  const activePath = items
    .filter((item) => isPathActive(pathname, item.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({ top: rect.top, left: rect.right + 8 });
  };

  const handleNavigate = (path: string) => {
    setPosition(null);
    navigate(path);
  };

  return (
    <React.Fragment>
      <NavTooltip title={title} open={open}>
        <ListItemButtonToggle onClick={handleOpen} selected={position !== null || !!activePath}>
          <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
          />
          <ChevronRightIcon fontSize="small" />
        </ListItemButtonToggle>
      </NavTooltip>
      <Popover
        open={position !== null}
        onClose={() => setPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={position ?? undefined}
        disableScrollLock
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 240, py: 0.5 },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <MenuList>
          {items.map((item) => (
            <MenuItem
              key={item.path}
              selected={item.path === activePath}
              onClick={() => handleNavigate(item.path)}
              sx={{
                py: 1.25, px: 2,
                '&.Mui-selected': { color: 'primary.main', fontWeight: 600, '& .MuiListItemIcon-root': { color: 'primary.main' } },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
              />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </React.Fragment>
  );
}

const budgetItems: NavGroupItem[] = [
  { label: 'Unidades Orçamentárias', path: '/unidades-orcamentarias', icon: <AccountTreeIcon fontSize="small" /> },
  { label: 'Programas Orçamentários', path: '/programas-orcamentarios', icon: <ListAltIcon fontSize="small" /> },
  { label: 'Ações Orçamentárias', path: '/acoes-orcamentarias', icon: <TrackChangesIcon fontSize="small" /> },
];

const portalStructureItems: NavGroupItem[] = [
  { label: 'Parâmetros do Portal', path: '/parametros', icon: <TuneIcon fontSize="small" /> },
  { label: 'Customizar Home', path: '/home', icon: <HomeIcon fontSize="small" /> },
  { label: 'Banners', path: '/banners', icon: <ViewCarouselIcon fontSize="small" /> },
  { label: 'Menus', path: '/menus', icon: <MenuIcon fontSize="small" /> },
  { label: 'Páginas Customizadas', path: '/paginas', icon: <ArticleIcon fontSize="small" /> },
];

const portalContentItems: NavGroupItem[] = [
  { label: 'Notícias', path: '/noticias', icon: <NewspaperIcon fontSize="small" /> },
  { label: 'Categorias de Perguntas', path: '/perguntas-respostas/categorias', icon: <CategoryIcon fontSize="small" /> },
  { label: 'Perguntas e Respostas', path: '/perguntas-respostas', icon: <QuizIcon fontSize="small" /> },
];

const portalTransparencyItems: NavGroupItem[] = [
  { label: 'Licitações', path: '/licitacoes', icon: <GavelIcon fontSize="small" /> },
  { label: 'Unidades Gestoras', path: '/unidades-gestoras', icon: <ApartmentIcon fontSize="small" /> },
  { label: 'Contratos', path: '/contratos', icon: <DescriptionIcon fontSize="small" /> },
  { label: 'Auditorias', path: '/auditorias', icon: <FactCheckIcon fontSize="small" /> },
  { label: 'Projetos de Governo', path: '/projetos-governo', icon: <AccountTreeIcon fontSize="small" /> },
  { label: 'Convênios e Repasses', path: '/convenios-repasses', icon: <HandshakeIcon fontSize="small" /> },
  { label: 'Contas Públicas', path: '/contas-publicas', icon: <AccountBalanceIcon fontSize="small" /> },
];

// Item direto da barra lateral (sem submenu)
function NavLinkItem({ item, open, pathname }: { item: NavGroupItem & { tooltip?: string }; open: boolean; pathname: string }) {
  return (
    <NavTooltip title={item.tooltip || item.label} open={open}>
      <ListItemButtonLink component={RouterLink} to={item.path} selected={isPathActive(pathname, item.path)}>
        <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
        />
      </ListItemButtonLink>
    </NavTooltip>
  );
}

const dashboardItem: NavGroupItem = { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon fontSize="small" /> };

const sdpItems: (NavGroupItem & { tooltip?: string })[] = [
  dashboardItem,
  { label: 'Remessas', path: '/remessas-publicacao', icon: <FileUploadIcon fontSize="small" /> },
  { label: 'Diário Oficial', path: '/diario-oficial', icon: <FileCopyIcon fontSize="small" /> },
];

const scpItems: (NavGroupItem & { tooltip?: string })[] = [
  dashboardItem,
  { label: 'Unidades', path: '/unidades-entidade', icon: <AccountTreeIcon fontSize="small" /> },
  { label: 'Int. Registro de Preço', tooltip: 'Intenção de Registro de Preço', path: '/intencao-registro-preco', icon: <PriceCheckIcon fontSize="small" /> },
  { label: 'Compras e Licitações', path: '/compras-licitacoes', icon: <ShoppingCartIcon fontSize="small" /> },
  { label: 'Contratos', path: '/contratos', icon: <DescriptionIcon fontSize="small" /> },
];

const sgeItems: NavGroupItem[] = [
  dashboardItem,
  { label: 'Partidos Políticos', path: '/partidos-politicos', icon: <GroupsIcon fontSize="small" /> },
  { label: 'Parlamentares', path: '/parlamentares', icon: <PersonIcon fontSize="small" /> },
  { label: 'Fornecedores', path: '/fornecedores', icon: <BusinessIcon fontSize="small" /> },
  { label: 'Emendas', path: '/emendas', icon: <AccountBalanceIcon fontSize="small" /> },
];

export function MainListItems({ open }: { open: boolean }) {
  const { pathname } = useLocation();
  const links = (items: (NavGroupItem & { tooltip?: string })[]) =>
    items.map((item) => <NavLinkItem key={item.path} item={item} open={open} pathname={pathname} />);

  return (
    <React.Fragment>
      {applicationData.moduleId === 1 && links(sdpItems)}
      {applicationData.moduleId === 9 && links(scpItems)}
      {applicationData.moduleId === 10 && (
        <React.Fragment>
          {links(sgeItems)}
          <NavGroup title="Orçamento" icon={<AccountBalanceWalletIcon fontSize="small" />} items={budgetItems} open={open} />
        </React.Fragment>
      )}
      {applicationData.moduleId === 3 && (
        <React.Fragment>
          {links([dashboardItem])}
          <NavGroup title="Estrutura do Portal" icon={<WebIcon fontSize="small" />} items={portalStructureItems} open={open} />
          <NavGroup title="Conteúdo" icon={<ArticleIcon fontSize="small" />} items={portalContentItems} open={open} />
          <NavGroup title="Transparência" icon={<VisibilityIcon fontSize="small" />} items={portalTransparencyItems} open={open} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export function SecondaryListItems({ logout, open }: { logout: () => void; open: boolean }) {
  const [transparencyLinkCopied, setTransparencyLinkCopied] = React.useState(false);
  const transparencyPortalPath = getTransparencyPortalPath();

  const handleCopyTransparencyLink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!transparencyPortalPath) return;
    const url = `${window.location.origin}${transparencyPortalPath}`;
    navigator.clipboard.writeText(url).then(() => {
      setTransparencyLinkCopied(true);
      setTimeout(() => setTransparencyLinkCopied(false), 2000);
    });
  };

  return (
    <React.Fragment>
      {applicationData.moduleId === 10 && transparencyPortalPath && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NavTooltip title="Painel da Transparência" open={open}>
            <ListItemButtonLink component={RouterLink} to={transparencyPortalPath} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, minWidth: 0 }}>
              <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
                <PublicIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Painel da Transparência"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500, noWrap: true }}
              />
            </ListItemButtonLink>
          </NavTooltip>
          <Tooltip title={transparencyLinkCopied ? 'Link copiado!' : 'Copiar link'}>
            <IconButton size="small" onClick={handleCopyTransparencyLink} sx={{ mr: 1.5 }}>
              {transparencyLinkCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <NavTooltip title="Sair" open={open}>
        <ListItemButtonToggle onClick={logout}>
          <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sair"
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
          />
        </ListItemButtonToggle>
      </NavTooltip>
    </React.Fragment>
  );
}
